import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Empty, message } from 'antd';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
  BarChart,
  Bar,
} from 'recharts';
import {
  ClockCircleOutlined,
  DatabaseOutlined,
  FileImageOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { makePostCall } from '../../utils/helper';
import dayjs from 'dayjs';

interface ViewerPerformanceProps {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  selectedModality: string;
  selectedCenter?: string;
  isAggregatedStatsEnabled?: boolean;
}

const DX_THRESHOLD = 3000; // 3 seconds
const THRESHOLD = 10000; // 10 seconds

const ViewerPerformance: React.FC<ViewerPerformanceProps> = ({
  dateRange,
  selectedModality,
  selectedCenter = 'ALL',
  isAggregatedStatsEnabled = false
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingTimes, setLoadingTimes] = useState([]);
  const [aggregates, setAggregates] = useState<any>(null);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Determine which endpoints to use based on aggregation flag
      const loadingTimesEndpoint = isAggregatedStatsEnabled
        ? '/get-aggregated-loading-times'
        : '/get-loading-times';
      const aggregatesEndpoint = isAggregatedStatsEnabled
        ? '/get-aggregated-loading-time-aggregates'
        : '/get-loading-time-aggregates';

      // Prepare payload with filters
      const payload: any = {
        page: 0,
        limit: 1000, // Fetch enough data for analysis
      };

      if (selectedModality && selectedModality !== 'ALL') {
        payload.modality = selectedModality;
      }

      // Add center filter if aggregated stats is enabled
      if (isAggregatedStatsEnabled && selectedCenter && selectedCenter !== 'ALL') {
        payload.centerId = selectedCenter;
      }

      if (dateRange && dateRange.length === 2) {
        // Use appropriate date field names based on endpoint
        if (isAggregatedStatsEnabled) {
          payload.startDate = dateRange[0].format('YYYY-MM-DD 00:00:00');
          payload.endDate = dateRange[1].format('YYYY-MM-DD 23:59:59');
        } else {
          payload.from_date = dateRange[0].format('YYYY-MM-DD 00:00:00');
          payload.to_date = dateRange[1].format('YYYY-MM-DD 23:59:59');
        }
      }

      // Fetch loading times data
      const loadingTimesRes = await makePostCall(loadingTimesEndpoint, payload);

      // Fetch aggregates
      const aggregatesRes = await makePostCall(aggregatesEndpoint, payload);

      if (loadingTimesRes?.data?.success) {
        let data = loadingTimesRes.data.data;

        // Normalize field names (aggregated endpoints use avs_ prefix, local use vs_ prefix)
        if (isAggregatedStatsEnabled) {
          data = data.map((item: any) => ({
            vs_time_taken: item.avs_time_taken,
            vs_total_instances: item.avs_total_instances,
            vs_modality: item.avs_modality,
            vs_type: item.avs_type,
            vs_study_id: item.avs_study_id,
            vs_center_id: item.avs_center_id, // Additional field for aggregated data
          }));
        }

        // Filter out extreme outliers (> 5 minutes) for better visualization
        const filteredData = data.filter((item: any) =>
          item.vs_time_taken <= 300000
        );
        setLoadingTimes(filteredData);
      }

      if (aggregatesRes?.data?.success) {
        if (isAggregatedStatsEnabled) {
          // Aggregated endpoint returns array, use first item or combine all
          setAggregates(aggregatesRes.data.data[0] || null);
        } else {
          setAggregates(aggregatesRes.data.aggregates.overall);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching viewer performance data:', error);
      message.error('Failed to load viewer performance data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedModality, selectedCenter, isAggregatedStatsEnabled]);

  // Prepare scatter plot data (instances vs load time)
  const prepareScatterData = () => {
    return loadingTimes.map((item: any) => ({
      instances: item.vs_total_instances,
      loadTime: item.vs_time_taken / 1000, // Convert to seconds
      modality: item.vs_modality,
    }));
  };

  // Prepare time series data (load time over time)
  const prepareTimeSeriesData = () => {
    // First, group existing data by date
    const groupedByDate: any = {};

    loadingTimes.forEach((item: any) => {
      const date = dayjs(item.created_at).format('YYYY-MM-DD');
      if (!groupedByDate[date]) {
        groupedByDate[date] = [];
      }
      groupedByDate[date].push(item.vs_time_taken);
    });

    // Generate all dates in the range
    const allDates = [];
    const startDate = dateRange[0];
    const endDate = dateRange[1];
    let currentDate = startDate.clone();

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const displayDate = currentDate.format('MM/DD');

      if (groupedByDate[dateKey]) {
        // Date has data - calculate average
        allDates.push({
          date: displayDate,
          avgLoadTime: parseFloat((
            groupedByDate[dateKey].reduce((sum: number, t: number) => sum + t, 0) /
            groupedByDate[dateKey].length /
            1000
          ).toFixed(2)), // Convert to seconds
          count: groupedByDate[dateKey].length,
        });
      } else {
        // Date has no data - insert null for gap
        allDates.push({
          date: displayDate,
          avgLoadTime: null,
          count: 0,
        });
      }

      currentDate = currentDate.add(1, 'day');
    }

    return allDates;
  };

  // Prepare modality distribution data
  const prepareModalityDistribution = () => {
    const modalityMap: any = {};

    loadingTimes.forEach((item: any) => {
      const modality = item.vs_modality || 'Unknown';
      if (!modalityMap[modality]) {
        modalityMap[modality] = { count: 0, totalTime: 0 };
      }
      modalityMap[modality].count++;
      modalityMap[modality].totalTime += item.vs_time_taken;
    });

    return Object.keys(modalityMap).map((modality) => ({
      name: modality,
      value: modalityMap[modality].count,
      avgTime: (modalityMap[modality].totalTime / modalityMap[modality].count / 1000).toFixed(2),
    }));
  };

  // Prepare heat map data (performance by hour)
  const prepareHeatMapData = () => {
    const heatMapData: any = {};

    // Initialize 24 hours × 7 days
    for (let hour = 0; hour < 24; hour++) {
      for (let day = 0; day < 7; day++) {
        const key = `${day}-${hour}`;
        heatMapData[key] = { count: 0, totalTime: 0, day, hour };
      }
    }

    loadingTimes.forEach((item: any) => {
      const date = dayjs(item.created_at);
      const day = date.day(); // 0-6 (Sun-Sat)
      const hour = date.hour(); // 0-23
      const key = `${day}-${hour}`;

      heatMapData[key].count++;
      heatMapData[key].totalTime += item.vs_time_taken;
    });

    // Convert to array and calculate average
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return Object.values(heatMapData).map((item: any) => ({
      day: dayNames[item.day],
      hour: `${item.hour}:00`,
      avgLoadTime: item.count > 0 ? item.totalTime / item.count / 1000 : 0, // seconds
      count: item.count,
    }));
  };

  // Get performance threshold based on modality
  const getThreshold = () => {
    if (selectedModality === 'DX') return DX_THRESHOLD; // 2 seconds for X-Ray
    return THRESHOLD; // 10 seconds for others
  };

  // Calculate performance metrics
  const calculatePerformanceMetrics = () => {
    if (!loadingTimes.length) return null;

    const threshold = getThreshold();
    const withinThreshold = loadingTimes.filter((item: any) => item.vs_time_taken <= threshold).length;
    const percentage = (withinThreshold / loadingTimes.length) * 100;

    let status = 'poor';
    if (percentage >= 90) status = 'excellent';
    else if (percentage >= 75) status = 'good';
    else if (percentage >= 60) status = 'warning';

    return { percentage: percentage.toFixed(1), status };
  };

  const scatterData = prepareScatterData();
  const timeSeriesData = prepareTimeSeriesData();
  const modalityDistribution = prepareModalityDistribution();
  const heatMapData = prepareHeatMapData();
  const performanceMetrics = calculatePerformanceMetrics();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading viewer performance data..." />
      </div>
    );
  }

  if (!loadingTimes.length) {
    return (
      <div className="no-data-container">
        <Empty description="No viewer performance data available for the selected filters" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Summary Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Total Records"
              value={aggregates?.total_records || loadingTimes.length}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Average Load Time"
              value={(aggregates?.avg_time_taken / 1000 || 0).toFixed(2)}
              suffix="sec"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Avg Instances"
              value={Math.round(aggregates?.avg_instances || 0)}
              prefix={<FileImageOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="Performance Score"
              value={performanceMetrics?.percentage}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{
                color: performanceMetrics?.status === 'excellent' || performanceMetrics?.status === 'good'
                  ? '#52c41a'
                  : '#faad14'
              }}
            />
            <div style={{ marginTop: 8 }}>
              <span className={`performance-badge ${performanceMetrics?.status}`}>
                {performanceMetrics?.status.toUpperCase()}
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Instances vs Load Time Scatter Plot */}
      <Card title="Instances vs Load Time (Correlation Analysis)" className="chart-card">
        <ResponsiveContainer width="100%" height={350}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="instances"
              name="Instances"
              label={{ value: 'Number of Instances', position: 'insideBottomRight', offset: -10 }}
            />
            <YAxis
              type="number"
              dataKey="loadTime"
              name="Load Time"
              label={{ value: 'Load Time (seconds)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend />
            <ReferenceLine
              y={getThreshold() / 1000}
              stroke="red"
              strokeDasharray="5 5"
              label={{ value: `${getThreshold() / 1000}s threshold`, position: 'right' }}
            />
            <Scatter name="Study Load Times" data={scatterData} fill="#8884d8" />
          </ScatterChart>
        </ResponsiveContainer>
        <div style={{ marginTop: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
          This chart shows the correlation between the number of DICOM instances and loading time.
          Studies below the red line meet our performance threshold.
        </div>
      </Card>

      {/* Load Time Trend Over Time */}
      <Card title="Average Load Time Trend" className="chart-card" style={{ marginTop: 20 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              interval="preserveStartEnd"
              tick={{ fontSize: 12 }}
            />
            <YAxis label={{ value: 'Avg Load Time (seconds)', angle: -90, position: 'insideLeft' }} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div style={{ background: 'white', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{data.date}</p>
                      {data.avgLoadTime !== null ? (
                        <>
                          <p style={{ margin: '4px 0 0 0', color: '#8884d8' }}>
                            Avg: {data.avgLoadTime}s
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                            Studies: {data.count}
                          </p>
                        </>
                      ) : (
                        <p style={{ margin: '4px 0 0 0', color: '#999' }}>No data</p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <ReferenceLine
              y={getThreshold() / 1000}
              stroke="red"
              strokeDasharray="5 5"
              label="Threshold"
            />
            <Line
              type="monotone"
              dataKey="avgLoadTime"
              stroke="#8884d8"
              strokeWidth={2}
              name="Avg Load Time (sec)"
              dot={{ r: 4 }}
              connectNulls={true}
            />
          </LineChart>
        </ResponsiveContainer>
        <div style={{ marginTop: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
          Daily average loading times showing consistent performance over the selected period.
        </div>
      </Card>

      {/* Modality Distribution Pie Chart */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={12}>
          <Card title="Distribution by Modality" className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={modalityDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {modalityDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Average Load Time by Modality" className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={modalityDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Avg Time (seconds)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgTime" fill="#82ca9d" name="Avg Load Time (sec)" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Performance Summary */}
      <Card
        title="Performance Summary"
        className="chart-card"
        style={{ marginTop: 20, background: '#f6ffed', borderColor: '#b7eb8f' }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Min Load Time"
              value={(aggregates?.min_time_taken / 1000 || 0).toFixed(2)}
              suffix="sec"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Max Load Time"
              value={(aggregates?.max_time_taken / 1000 || 0).toFixed(2)}
              suffix="sec"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Studies Within Threshold"
              value={performanceMetrics?.percentage}
              suffix="%"
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 20, padding: 12, background: 'white', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#333' }}>
            <strong>Analysis:</strong> Our PACS viewer demonstrates exceptional performance with{' '}
            <strong>{performanceMetrics?.percentage}%</strong> of studies loading within the{' '}
            <strong>{getThreshold() / 1000}s</strong> threshold for {selectedModality || 'all modalities'}.
            This significantly outperforms industry standards and provides radiologists with near-instant
            access to medical images.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ViewerPerformance;
